import { trackEvent } from '@pm/analytics';
import { LabWork } from '@pm/graphql';
import { ButtonLink, Icon, IconButton } from '@pm/ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useEnvironment } from '../../hooks/env';
import { isLabReq } from './type-guards';
import { useMarkRequisitionViewed } from './useMarkRequisitionViewed';
import { toLabRequisitionUrl } from './utils';

type LabActionsProps = {
  labWork: LabWork;
  isNew?: boolean;
};

export const LabActions = ({ labWork, isNew = false }: LabActionsProps) => {
  const { t } = useTranslation(['labs', 'global']);
  const { apiHost } = useEnvironment();
  const canInteractWithAttachments = isLabReq(labWork);
  const downloadUrl =
    canInteractWithAttachments && labWork.attachments?.length
      ? labWork.attachments[0].downloadUrl
      : null;
  const { markAsViewed, loading: viewedAtLoading } =
    useMarkRequisitionViewed(labWork);

  const icon = (
    <Icon name="download" size="large" color="text-content-positive" />
  );
  const sharedButtonProps = {
    to: toLabRequisitionUrl(downloadUrl ?? '', apiHost),
    target: '_blank',
    rel: 'noopener noreferrer',
    'aria-label': t('ViewResults'),
    intent: 'tertiary',
    disabled: viewedAtLoading,
    onClick: () => {
      markAsViewed();
      trackEvent('Button Clicked', {
        buttonText: t('labs:DownloadLabel'),
        buttonName: 'download_lab_requisition',
        currentPageName: 'Labs',
        currentPath: window.location.pathname,
      });
    },
  } as const;

  return (
    <div aria-label={t('labs:ActionsLabel')}>
      {downloadUrl &&
        (isNew ? (
          <div className="ml-auto">
            <ButtonLink
              {...sharedButtonProps}
              trailingIcon={icon}
              size="xSmall"
            >
              {t('labs:Download')}
            </ButtonLink>
          </div>
        ) : (
          <IconButton intent="ghost" size="xSmall">
            <Link download {...sharedButtonProps}>
              {icon}
            </Link>
          </IconButton>
        ))}
    </div>
  );
};
